import styled from 'styled-components';
import {
  useState,
  FC,
  useCallback,
  ChangeEventHandler,
  KeyboardEventHandler,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import { Typography, TypographyProps, TextField, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

type EditableTitleProps = TypographyProps & {
  title: string;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
  error?: ReactNode;
  isValid: (value: string) => boolean;
};

export const EditableTitle: FC<EditableTitleProps> = ({ title, onSave, disabled, error, isValid, ...restProps }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [titleWidth, setTitleWidth] = useState(0);
  const titleRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setValue(event.target.value);
  }, []);

  const handleSaveButtonClick = useCallback(async () => {
    if (isValid(value)) {
      await onSave(value);

      setEditing(false);
    }
  }, [value, isValid, onSave]);

  const handleEditClick = useCallback(() => {
    if (!disabled && !editing) {
      setEditing(true);
    }
  }, [disabled, editing]);

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.key === 'Enter') {
        handleSaveButtonClick();
      }
    },
    [handleSaveButtonClick]
  );

  useEffect(() => {
    if (titleRef.current) {
      const titleWidth = Math.floor(titleRef.current.getBoundingClientRect().width) + 5;
      setTitleWidth(titleWidth);
    }
  }, [title, editing]);

  return (
    <>
      <EditableTitleContainer
        width={editing ? `${Math.ceil(titleWidth)}px` : 'auto'}
        ref={titleRef}
        onClick={handleEditClick}
      >
        {editing ? (
          <>
            <TextField
              variant="standard"
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              className="text-input"
              disabled={disabled}
            />

            {!disabled && (
              <IconButton aria-label="save" onClick={handleSaveButtonClick}>
                <CheckIcon />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <Title {...restProps} disabled={disabled} fontWeight="500">
              {title}
            </Title>
          </>
        )}
      </EditableTitleContainer>

      {error}
    </>
  );
};

const EditableTitleContainer = styled.div<{ width: string }>`
  display: flex;
  align-items: center;
  cursor: pointer;

  ${(p) => `width: ${p.width}`};

  .text-input {
    width: 100%;
    min-width: 290px;
  }
`;

const Title = styled(Typography)<{ disabled?: boolean }>`
  ::after {
    border-bottom: 1px dashed rgba(0, 0, 0, 0.5);
    display: block;
    position: relative;
    top: 0;
    opacity: 0;
    content: '';
  }

  ${EditableTitleContainer}:hover & {
    ::after {
      opacity: 1;
    }
  }

  ${(p) => p.disabled && `opacity: 0.5`};
`;
